import { initConfig, setConfigByKey, track } from '@bigo/logger';
import { TrackParams } from 'models/logger';
import { TrackConfig } from '@bigo/logger/build/src/interface';
import { getSiteEnv } from './global';
import { canUpload, NO_EMITS } from './sentry';
import { customLocalStorage } from './storage';

/** 是否已经初始化配置了 */
let isLoggerInited = false;

const currentEnv = getSiteEnv();

/** 兜底国家码，有些旧版本浏览器可能拿不到navigator?.language */
const localCountryCode =
  customLocalStorage.getItem('DSP_COUNTRY_CODE') ||
  navigator?.language ||
  // @ts-ignore
  navigator?.userLanguage ||
  // @ts-ignore
  navigator?.browserLanguage;

const globalReportConfig = {
  // track需要这个参数，使用禅道号
  act_id: 78946,
  device_id: navigator?.userAgent?.toLowerCase?.(),
  source: `ads-dsp-frontend-${currentEnv}`,
  uid: window.DSP_USER_ID || `ADS-DSP-FRONTEND-UID-${+new Date()}-${Math.random().toFixed(5)}`,
  country_code: window.DSP_USER_COUNTRY || localCountryCode,
};

// ff 隐私模式 window?.indexedDB 为空
// 目前的 bigo-logger 不支持在 window?.indexedDB 无法使用时记录日志
const needLog = !!window?.indexedDB || process.env.NEED_LOG;

function initLogger(uid: string | number, country: string) {
  if (!needLog) {
    return;
  }

  initConfig({
    _devSend: !!process.env.NEED_LOG,
    globalReportConfig: {
      ...globalReportConfig,
      uid: uid || `ADS-DSP-FRONTEND-UID-${+new Date()}-${Math.random().toFixed(5)}`,
      // 一般来说国家码不会变，对于还没拿到接口国家码的，使用本地存储的国家码
      country_code: country || localCountryCode,
    },
    // 重试次数
    logTryTimes: 3,
    // 日志条数达到多少条时上报。
    // bigo-logger有bug，这个配置实际没起作用，代码逻辑实际上取了 logTryTimes，这里保持一致。
    autoReportCount: 3,
    requestDisableFilter(url: string) {
      // blob 请求添加 requestid 会导致加载失败，而且 blob 也完全没有必要上报
      if (/^blob/.test(`${url}`)) {
        return true;
      }

      // 过滤 sentry 请求
      const blocklist = ['//frontmon-sysop.bigo.sg/api'];

      if (blocklist.some((path) => url.includes(path))) {
        return true;
      }

      return false;
    },
  });

  isLoggerInited = true;

  setTimeout(() => {
    const originError = window.onerror;
    window.onerror = function newError(...args) {
      // 粗略判断是不是控制台报错
      const triggerByDevtools =
        `${args[0]}`.includes('Uncaught') &&
        // 控制台直接 throw new Error
        (args[1] === '' ||
          // 控制台其他奇怪代码导致的报错
          args[1] === window.location.href);

      if (triggerByDevtools) {
        return;
      }

      if (NO_EMITS.some((str) => `${args[0]}`.includes(str))) {
        return;
      }

      // 使用 canUpload 进行拦截，防止死循环
      // 如果同时使用了logger和sentry，则会导致 错误触发 10 次之后，sentry 上报频率急剧减少，这种情况影响不大
      if (!canUpload(`onerror: ${args[0]}`)) {
        return;
      }

      originError?.apply(window, args);
    };
    const originUnHandle = window.onunhandledrejection;
    window.onunhandledrejection = function newUnHandle(...args) {
      if (NO_EMITS.some((str) => `${args[0]?.reason}`.includes(str))) {
        return;
      }

      if (!canUpload(`onunhandledrejection: ${args[0]?.reason}`)) {
        return;
      }

      originUnHandle?.apply(window, args);
    };
  }, 50);
}

/** track 日志，立即上报 */
export async function trackToLogger(params: TrackParams) {
  try {
    if (!needLog) {
      return;
    }
    if (!isLoggerInited) {
      // track 依赖 globalReportConfig
      setConfigByKey('globalReportConfig', globalReportConfig);
      isLoggerInited = true;
    }

    const payload: TrackConfig = {
      // track需要这个参数，这里用禅道地址
      act_id: 78946,
      log_action: params.logAction,
      log_content: JSON.stringify(params.logContent, undefined, 0),
    };
    await track(payload);
  } catch (err) {
    console.error('[Logger] track error', err);
    // err?.message && catchLoggerError(err.message, { error: err });
  }
}

export { initLogger };
